import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DataTable,
  EmptyState,
  Field,
  Icon,
  Input,
  Panel,
  Select,
  Tag,
  useErrorDialog,
  type Column,
} from '@dayon/design-system';
import { api, ApiRequestError } from '../../lib/api.ts';
import { DISPLAY_TZ } from '../access/format.ts';
import {
  amountText,
  OPERATION_LABEL,
  TRANSACTION_PROVIDERS,
  transactionsPath,
  TXN_STATUS,
  type Filters,
  type ProviderTransaction,
  type ProviderTransactionPage,
} from './provider-transactions.ts';
import { transactionsEmpty, transactionsLead } from './scope-words.ts';

/**
 * A live provider's seamless-wallet traffic, one row per callback we kept
 * (SA-124): every bet, win, loss, cancel and balance adjustment SA Gaming sent,
 * with its txnid, amount and what we did with it.
 *
 * READ-ONLY, and nothing here is invented: the rows are
 * `GET /api/games/provider-transactions` (players.view), newest first, paged by
 * the server's cursor. The request and response bodies are never sent to this
 * screen — they can carry the player's token — so they are not shown.
 *
 * A reader restricted to some brands is sent only their players' callbacks
 * (SEC-002), and the answer says so; the notice and the empty state take their
 * words from it (scope-words.ts).
 */

const WHEN = new Intl.DateTimeFormat('en-PH', {
  timeZone: DISPLAY_TZ,
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const EMPTY: Filters = { provider: 'SA', playerRef: '', txnId: '', from: '', to: '' };

const columns: Column<ProviderTransaction>[] = [
  {
    key: 'when',
    header: 'When',
    render: (t) => <span className="text-muted">{WHEN.format(new Date(t.createdAt))}</span>,
  },
  { key: 'txn', header: 'Txn ID', render: (t) => <code>{t.externalId}</code> },
  {
    key: 'operation',
    header: 'Operation',
    render: (t) => OPERATION_LABEL[t.operation] ?? t.operation,
  },
  { key: 'amount', header: 'Amount', numeric: true, render: (t) => amountText(t.amount) },
  {
    key: 'status',
    header: 'Outcome',
    render: (t) => {
      if (t.status === null) return <span className="text-muted">—</span>;
      const s = TXN_STATUS[t.status];
      return <Tag tone={s?.tone ?? 'quiet'}>{s?.label ?? t.status}</Tag>;
    },
  },
  { key: 'round', header: 'Round', secondary: true, render: (t) => t.roundRef ?? '—' },
  { key: 'game', header: 'Game', secondary: true, render: (t) => t.gameCode ?? '—' },
  { key: 'player', header: 'Player', secondary: true, render: (t) => t.playerRef ?? '—' },
  {
    key: 'attempts',
    header: 'Attempts',
    numeric: true,
    render: (t) => (t.attempts > 1 ? <Tag tone="outline">{t.attempts}</Tag> : String(t.attempts)),
  },
  {
    key: 'lastAttempt',
    header: 'Last attempt',
    secondary: true,
    render: (t) =>
      t.lastAttemptAt === null ? (
        '—'
      ) : (
        <span className="text-muted">{WHEN.format(new Date(t.lastAttemptAt))}</span>
      ),
  },
  {
    key: 'updated',
    header: 'Last changed',
    secondary: true,
    render: (t) =>
      t.updatedAt === null ? (
        '—'
      ) : (
        <span className="text-muted">{WHEN.format(new Date(t.updatedAt))}</span>
      ),
  },
];

export function ProviderTransactions() {
  const { showError } = useErrorDialog();
  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const [rows, setRows] = useState<ProviderTransaction[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  /* "No rows" and "could not ask" are different facts (see Games.tsx). */
  const [failed, setFailed] = useState(false);
  /* Whether the rows are only those of the reader's brands, from the answer
     they came in. Null until an answer is here. */
  const [brandRestricted, setBrandRestricted] = useState<boolean | null>(null);

  const load = useCallback(
    async (filters: Filters, cursor: string | null) => {
      try {
        const page = await api.get<ProviderTransactionPage>(transactionsPath(filters, cursor));
        setRows((current) => (cursor ? [...current, ...page.items] : page.items));
        setNextCursor(page.nextCursor);
        setBrandRestricted(page.brandRestricted);
        setFailed(false);
      } catch (error) {
        if (!cursor) {
          setRows([]);
          setFailed(true);
        }
        showError({
          title: 'Could not load provider transactions',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      }
    },
    [showError],
  );

  useEffect(() => {
    setLoading(true);
    void load(applied, null).finally(() => setLoading(false));
  }, [applied, load]);

  const set = (key: keyof Filters) => (e: { target: { value: string } }) =>
    setDraft((d) => ({ ...d, [key]: e.target.value }));

  /* A real zero needs an answer to be one: until there is one, the table says it is not loaded. */
  const empty = brandRestricted === null ? null : transactionsEmpty(brandRestricted);

  return (
    <>
      <div className="notice notice-quiet">
        <Icon name="notebook" />
        <div>
          {transactionsLead(brandRestricted)} A retried callback is one row, and Attempts counts
          every delivery, so a callback the provider keeps resending stands out. A retry never posts again: one
          already applied changes nothing, and one that was refused is checked again, so a bet that
          arrived late can still be handed back. Times are Manila time.
        </div>
      </div>

      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied(draft);
        }}
      >
        <Field label="Provider">
          {({ id }) => (
            <Select id={id} value={draft.provider} onChange={set('provider')}>
              {TRANSACTION_PROVIDERS.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Player ref">
          {({ id }) => <Input id={id} value={draft.playerRef} onChange={set('playerRef')} />}
        </Field>
        <Field label="Txn ID">
          {({ id }) => <Input id={id} value={draft.txnId} onChange={set('txnId')} />}
        </Field>
        <Field label="From">
          {({ id }) => <Input id={id} type="date" value={draft.from} onChange={set('from')} />}
        </Field>
        <Field label="To">
          {({ id }) => <Input id={id} type="date" value={draft.to} onChange={set('to')} />}
        </Field>
        <div className="row" style={{ alignSelf: 'flex-end' }}>
          <Button type="submit" variant="primary">
            Search
          </Button>
          <Button
            onClick={() => {
              setDraft(EMPTY);
              setApplied(EMPTY);
            }}
          >
            Clear
          </Button>
        </div>
      </form>

      <Panel>
        {loading ? (
          <div className="skeleton" style={{ height: '200px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="Provider wallet callbacks with their txnid, operation, amount and outcome"
            columns={columns}
            rows={rows}
            rowKey={(t) => t.id}
            empty={
              failed || empty === null ? (
                <EmptyState icon="warning" title="Not loaded">
                  The transactions could not be read, so this is not a zero. Search again to retry.
                </EmptyState>
              ) : (
                <EmptyState icon="notebook" title={empty.title}>
                  {empty.body}
                </EmptyState>
              )
            }
          />
        )}
        {nextCursor && !loading && (
          <div className="row" style={{ justifyContent: 'center' }}>
            <Button
              busy={loadingMore}
              onClick={() => {
                setLoadingMore(true);
                void load(applied, nextCursor).finally(() => setLoadingMore(false));
              }}
            >
              Load more
            </Button>
          </div>
        )}
      </Panel>
    </>
  );
}
